document.addEventListener('DOMContentLoaded', async () => {
	/**
	 * Loads Guardian data on page load.
	 * Checks session storage for existing data, fetches new data if expired,
	 * and updates global Guardian state and UI accordingly.
	 */
	const storedGuardianData = JSON.parse(getSessionStorage('GGG_data'));
	const guardiansDataScript = document.getElementById('guardians-data-script');
	const guardianDataShelfLife = '00:10:00:000'; // HH:MM:SS:MsMsMs format
	const dataExpired = storedGuardianData?.timestamp
		? isTimeExceeded(storedGuardianData.timestamp, guardianDataShelfLife)
		: true;

	if (guardiansDataScript.dataset.isCustomer) {
		console.log('We got a customer here!');
		if (!dataExpired) {
			console.log('$$$$$$$$$ Stored GGG data found! Updating UI!');
			console.log(storedGuardianData);
			setGGGWinVarAndPub(storedGuardianData);
		} else {
			console.log('$$$$$$$$$ Either no stored GGG data found or it is out of date. Fetching data…');
			try {
				let guardiansRes = await cusaFetch('/apps/takeaction/account_info', 'GET');

				if (guardiansRes.response.ok) {
					setGGGWinVarAndPub(guardiansRes.data);
				} else {
					throw new Error();
				}
			} catch (error) {
				console.error(error);
			}
		}
	} else {
		console.log('⚠️ User not logged in.');
	}
});

/**
 * Determines if the provided timestamp has exceeded the allowed shelf life.
 * @param {number} timestamp - The original data timestamp in milliseconds.
 * @param {string} min - The time limit in HH:MM:SS:MsMsMs format.
 * @returns {boolean} - True if the time is exceeded; otherwise false.
 */
const isTimeExceeded = (timestamp, min) => {
	if (typeof timestamp !== 'number' || isNaN(timestamp)) {
		console.warn('Invalid GGG_data timestamp: must be a number in milliseconds');
		return false;
	}

	if (!/^\d{2}:\d{2}:\d{2}:\d{3}$/.test(min)) {
		console.warn('Invalid min time format: must be "HH:MM:SS:MsMsMs"');
		return false;
	}

	const [hh, mm, ss, ms] = min.split(':').map(Number);
	if ([hh, mm, ss, ms].some((num) => isNaN(num) || num < 0)) {
		console.warn('Invalid min time values: all parts must be non-negative numbers');
		return false;
	}

	const minMs = hh * 60 * 60 * 1000 + mm * 60 * 1000 + ss * 1000 + ms;

	const elapsedTime = Date.now() - timestamp;
	const timeRemaining = minMs - elapsedTime;
	const minutes = Math.floor(timeRemaining / 60000);
	const seconds = Math.floor((timeRemaining % 60000) / 1000);
	console.log(`Time remaining for data to be good: ${minutes} minutes and ${seconds} seconds`);

	return Date.now() - timestamp > minMs;
};

/**
 * Removes a specified award from the guardianData object and updates storage.
 * Useful for testing award re-locking behavior.
 * @param {string} awardToRemove - The handle of the award to remove.
 * @returns {Object|null} - Updated guardianData object if successful, otherwise null.
 */
const removeAward = (awardToRemove) => {
	if (!window.guardianData || !Array.isArray(window.guardianData.awards)) {
		console.error('Error: window.guardianData or its awards array is not defined.');
		return null;
	}

	const originalLength = window.guardianData.awards.length;

	window.guardianData.awards = window.guardianData.awards.filter((award) => award !== awardToRemove);
	setLocalStorage('GGG_previousUnlockedAwards', JSON.stringify(window.guardianData.awards));

	if (window.guardianData.awards.length === originalLength) {
		console.error(`Error: Award value "${awardToRemove}" not found in the awards array.`);
	}

	setGGGWinVarAndPub(window.guardianData);

	return window.guardianData;
};

/**
 * Updates global Guardian data and publishes it for subscribers.
 * Also stores data in sessionStorage with a timestamp.
 * @param {Object} storedOrResData - The full response or session-stored object.
 */
const setGGGWinVarAndPub = (storedOrResData) => {
	// Use the member_summary if available.
	let data = storedOrResData.member_summary || storedOrResData;
	if (storedOrResData.timestamp) {
		window.guardianData = { timestamp: storedOrResData.timestamp, ...data };
	} else {
		window.guardianData = { timestamp: Date.now(), ...data };
	}

	/**
	 * Stores data in sessionStorage under a given key.
	 * @param {string} key - The key under which to store the data.
	 * @param {string} value - The stringified value to store.
	 */
	setSessionStorage('GGG_data', JSON.stringify(window.guardianData));

	console.log('>>>>> in setGGGWinVarAndPub! >>>>>', window.guardianData);
	publish(PUB_SUB_EVENTS.guardiansAction, data);
	formatStats();
};

/**
 * Updates all DOM elements with Guardian stats using latest session data.
 */
const updateGuardiansStats = () => {
	const allGuardianStatEls = document.querySelectorAll('[data-guardian-stat]');
	for (const statEl of allGuardianStatEls) {
		statEl.textContent = getGuardianStats(statEl.dataset.guardianStat);
		formatStatEl(statEl);
	}
};

/**
 * Retrieves a specific stat value from global Guardian data.
 * @param {string} guardianStatType - The stat key to retrieve.
 * @returns {number|string|null} - The stat value or null if unavailable.
 */
getGuardianStats = (guardianStatType) => {
	if (window.guardianData.hasOwnProperty('points_earned')) {
		window.guardianData = window.guardianData.member_summary;
	}

	switch (guardianStatType) {
		case 'available_good_points':
			return window.guardianData.good_points.available;
		case 'lifetime_good_points':
			return window.guardianData.good_points.lifetime;
		case 'total_clicks':
			return window.guardianData.total_clicks;
		case 'people_fed':
			return window.guardianData.impact.people_fed;
		case 'animals_fed':
			return window.guardianData.impact.animals_fed;
		case 'trees_planted':
			return window.guardianData.impact.trees_planted * 250;
		case 'current_streak':
			return window.guardianData.streak.current;
		case 'longest_streak':
			return window.guardianData.streak.longest;
		case 'petitions_signed':
			return window.guardianData.petitions_signed;
		default:
			return null;
	}
};

/**
 * Subscribes to Guardian data updates and refreshes UI accordingly.
 */
subscribe(PUB_SUB_EVENTS.guardiansAction, (guardianData) => updateGuardiansStats(guardianData));
